'use client';

import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { encryptPasswordAction } from '@/app/actions/encryptPassword';
import AuthToast from '@/components/V2/Popup/AuthToast';
import { getClientEnv } from '@/config/clientEnv';
import { CustomerTypes } from '@/helpers/constants/shopping';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { useAppContext } from '@/layouts/AppProvider';
import { AUTH_MODAL_TOAST_CONTAINER } from '@/layouts/ToastWrapper/containerIds';
import { useCustomerResetPasswordMutation, useSendEmailForForgetPasswordMutation } from '@/store/api/authentication';
import {
  ForgotPasswordStep,
  removeForgotPasswordFromLocalStorageState,
  resetForgotPasswordState,
  setCurrentStep as setCurrentStepAction,
  setCustomerDetail as setCustomerDetailAction,
  setCustomerEmail as setCustomerEmailAction,
} from '@/store/slices/forgotPasswordSlice';

import type { RootState } from '@/store';

const SUCESSSTYPE = 'success';
const ERRORTYPE = 'error';

interface ForgotPasswordContextProps {
  currentStep: ForgotPasswordStep;
  customerEmail: string;
  error: string;
  isLoading: boolean;
  customerDetail: { customerID: number; customerTypeID: number } | null;

  handleForgotPasswordSubmit: (email: string) => void;
  handleEnterCodeVerify: (message: string) => void;
  handleEnterCodeBack: () => void;
  handleResetPasswordSubmit: (password: string) => void;
  handleBackToLogin: () => void;
}

const ForgotPasswordContext = createContext<ForgotPasswordContextProps | undefined>(undefined);

export const ForgotPasswordProvider = ({ children }: { children: ReactNode }) => {
  const { generateURL, webalias } = useWebAlias();
  const { isBackOffice } = useAppContext();
  const dispatch = useDispatch();

  const { currentStep, customerEmail, customerDetail } = useSelector((state: RootState) => state.slices.forgotPassword);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [sendEmailForForgetPassword] = useSendEmailForForgetPasswordMutation();
  const [resetCustomerPassword] = useCustomerResetPasswordMutation();
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleForgotPasswordSubmit = async (email: string) => {
    try {
      setIsLoading(true);
      setError('');

      const res = await sendEmailForForgetPassword({ email }).unwrap();

      if (res?.success) {
        const { data, message } = res;

        if (data.customerTypeID !== CustomerTypes.PET_PRO && isBackOffice) {
          setError('This email is registered with pawtree.com.');
          return null;
        }

        dispatch(setCustomerEmailAction(email));
        dispatch(
          setCustomerDetailAction({
            customerID: data.customerID || 0,
            customerTypeID: data.customerTypeID || 0,
          })
        );
        dispatch(setCurrentStepAction('verify'));

        if (message) {
          const ifErrorMessage = message.toLowerCase().includes('failed') || message.toLowerCase().includes('not sent');
          showToastMessage(message, ifErrorMessage ? ERRORTYPE : SUCESSSTYPE);
        } else {
          showToastMessage('Your reset code has been sent successfully.', SUCESSSTYPE);
        }
      } else {
        if (res?.message) {
          showToastMessage(res.message, ERRORTYPE);
        } else {
          showToastMessage('Failed to send reset security code.', ERRORTYPE);
        }
      }
    } catch (err: any) {
      showToastMessage(err?.message || 'Something went wrong.', ERRORTYPE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnterCodeVerify = async (message: string) => {
    if (message === '2FA verified.') {
      dispatch(setCurrentStepAction('reset'));
    } else {
      showToastMessage(message || 'Invalid or expired verification code.', ERRORTYPE);
    }
  };

  const handleResetPasswordSubmit = async (password: string) => {
    try {
      setIsLoading(true);
      if (!customerDetail) {
        showToastMessage('Customer Details not found.', ERRORTYPE);
        return null;
      }
      const encryptedPassword = await encryptPasswordAction(password);
      if (!encryptedPassword) {
        showToastMessage('Failed to encrypt password. Please try again.', ERRORTYPE);
        return;
      }

      const res = await resetCustomerPassword({
        customerId: customerDetail.customerID,
        newPassword: encryptedPassword,
      }).unwrap();

      if (res?.success) {
        showToastMessage(
          res.message || 'Password successfully changed! Please wait while you are redirected back to login.',
          SUCESSSTYPE
        );

        let loginUrl = generateURL('/account/login', webalias);
        if (customerDetail?.customerTypeID === CustomerTypes.PET_PRO) {
          loginUrl = `${getClientEnv().BACKOFFICE_ORIGIN}/login`;
        }

        dispatch(removeForgotPasswordFromLocalStorageState());

        setTimeout(() => {
          window.location.href = loginUrl;
        }, 500);
      } else {
        showToastMessage(res?.message || 'Failed to reset password.', ERRORTYPE);
      }
    } catch (err: any) {
      const message = err?.data?.message;
      showToastMessage(message, ERRORTYPE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnterCodeBack = () => dispatch(setCurrentStepAction('forgot'));

  const handleBackToLogin = () => {
    dispatch(resetForgotPasswordState());
    let loginUrl = generateURL('/account/login', webalias);
    if (isBackOffice) {
      loginUrl = `${getClientEnv().BACKOFFICE_ORIGIN}/login`;
    }
    window.location.href = loginUrl;
  };

  const showToastMessage = (message: string, type: 'error' | 'success' = 'success') => {
    toast(({ closeToast }) => <AuthToast type={type} message={message} closeToast={closeToast} />, {
      containerId: AUTH_MODAL_TOAST_CONTAINER,
    });
  };

  return (
    <ForgotPasswordContext.Provider
      value={{
        currentStep,
        customerEmail,
        customerDetail,
        isLoading,
        error,
        handleForgotPasswordSubmit,
        handleEnterCodeVerify,
        handleEnterCodeBack,
        handleResetPasswordSubmit,
        handleBackToLogin,
      }}
    >
      {children}
    </ForgotPasswordContext.Provider>
  );
};

export const useForgotPasswordContext = (): ForgotPasswordContextProps => {
  const context = useContext(ForgotPasswordContext);
  if (!context) {
    throw new Error('useForgotPasswordContext must be used within a ForgotPasswordProvider');
  }
  return context;
};
